import { resolveSaqFormDictionary } from "../dictionaries/saq/saq_dictionary_registry";
import { rocFormDictionary } from "../dictionaries/roc/form_dictionary";
import { normalizeDescopedSelections } from "./product_license_descoped_selections.util";

export const ALWAYS_IN_SCOPE_ROC_SECTION_7_REQUIREMENTS = new Set<string>(["12.5.2"]);

export const ROC_REDUCED_SCOPE_FORCE_INCLUDE_REQUIREMENTS = new Set<string>([
    "3.6.1.1",
    "3.7.9",
    "8.2.3",
    "8.3.10",
    "8.3.10.1",
    "10.7.1",
    "11.4.6",
    "11.4.7",
    "11.5.1.1",
    "12.4.1",
    "12.4.2",
    "12.4.2.1",
    "12.5.2.1",
    "12.5.3",
    "12.9.1",
    "12.9.2",
    "A2.1.2",
    "A2.1.3"
]);

const SUPPORTED_ROC_REDUCED_SCOPE_SAQ_TYPES = new Set<string>([
    "SAQ A",
    "SAQ A (EP)",
    "SAQ B",
    "SAQ B (IP)",
    "SAQ C",
    "SAQ C (VT)",
    "SAQ D",
    "SAQ D (SP)",
    "SAQ P2PE",
    "SAQ SPoC"
]);

export function normalizeRocSection7RequirementTarget(value: unknown): string | null {
    if (typeof value !== "string") {
        return null;
    }

    const normalized = value.trim().toUpperCase().replace(/_/g, ".");
    return normalized || null;
}

export function isAlwaysInScopeRocSection7RequirementTarget(value: unknown): boolean {
    const target = normalizeRocSection7RequirementTarget(value);
    return !!target && ALWAYS_IN_SCOPE_ROC_SECTION_7_REQUIREMENTS.has(target);
}

export interface RocReducedScopeAdapter {
    normalizeSelections(value: unknown): string[];
    resolveSection7RequirementTargets(value: unknown): string[];
}

export function normalizeRocReducedScopeSelections(value: unknown): string[] {
    return normalizeDescopedSelections(value)
        .filter((selection) => SUPPORTED_ROC_REDUCED_SCOPE_SAQ_TYPES.has(selection));
}

export function resolveRocSection7RequirementTargets(
    descopedSelections: unknown,
    dictionary = rocFormDictionary
): string[] {
    const requirements = dictionary.section_7?.requirements || {};
    const selectedSaqTypes = normalizeRocReducedScopeSelections(descopedSelections);
    if (!selectedSaqTypes.length) {
        return Object.keys(requirements);
    }

    const selectedSaqRequirementTargets = new Set<string>();
    selectedSaqTypes.forEach((saqType) => {
        const saqRequirements = resolveSaqFormDictionary(saqType).section_2b?.requirements || {};
        Object.keys(saqRequirements).forEach((target) => {
            const normalizedTarget = normalizeRocSection7RequirementTarget(target);
            if (normalizedTarget) {
                selectedSaqRequirementTargets.add(normalizedTarget);
            }
        });
    });

    const includesSaqDMerchant = selectedSaqTypes.includes("SAQ D");
    return Object.keys(requirements).filter((target) => {
        const normalizedTarget = normalizeRocSection7RequirementTarget(target);
        if (!normalizedTarget || isAlwaysInScopeRocSection7RequirementTarget(normalizedTarget)) {
            return true;
        }

        if (
            includesSaqDMerchant &&
            ROC_REDUCED_SCOPE_FORCE_INCLUDE_REQUIREMENTS.has(normalizedTarget)
        ) {
            return true;
        }

        return !selectedSaqRequirementTargets.has(normalizedTarget);
    });
}

export function resolveRocEvidenceRequirementTargets(
    descopedSelections: unknown,
    dictionary = rocFormDictionary
): string[] {
    const requirements = dictionary.section_7?.requirements || {};
    const selectedSaqTypes = normalizeRocReducedScopeSelections(descopedSelections);
    if (!selectedSaqTypes.length) {
        return Object.keys(requirements);
    }

    const selectedSaqRequirementTargets = new Set<string>();
    selectedSaqTypes.forEach((saqType) => {
        const saqRequirements = resolveSaqFormDictionary(saqType).section_2b?.requirements || {};
        Object.keys(saqRequirements).forEach((target) => {
            const normalizedTarget = normalizeRocSection7RequirementTarget(target);
            if (normalizedTarget) {
                selectedSaqRequirementTargets.add(normalizedTarget);
            }
        });
    });

    const includesSaqDMerchant = selectedSaqTypes.includes("SAQ D");
    return Object.keys(requirements).filter((target) => {
        const normalizedTarget = normalizeRocSection7RequirementTarget(target);
        return !normalizedTarget ||
            isAlwaysInScopeRocSection7RequirementTarget(normalizedTarget) ||
            (includesSaqDMerchant && ROC_REDUCED_SCOPE_FORCE_INCLUDE_REQUIREMENTS.has(normalizedTarget)) ||
            selectedSaqRequirementTargets.has(normalizedTarget);
    });
}

export function createRocReducedScopeAdapter(
    dictionary = rocFormDictionary
): RocReducedScopeAdapter {
    return {
        normalizeSelections: normalizeRocReducedScopeSelections,
        resolveSection7RequirementTargets: (descopedSelections) =>
            resolveRocSection7RequirementTargets(descopedSelections, dictionary)
    };
}
